import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { useTheme } from '@mui/material/styles'

import { useCategoryStore, useGlobalCopyStore } from '../../common'
import { getIncomeData, getSpendingData } from '../utils/SpendingData'

import SpendingTabLabel from './SpendingTabLabel'
import SpendingDonut from './SpendingDonut'
import SpendingLegend from './SpendingLegend'

const MiniSpendingChart = () => {
  const theme = useTheme()
  const { goals: goalsCopy, spending: spendingCopy } = useGlobalCopyStore()
  const { categories, dateRangeCategoryTotals } = useCategoryStore()

  const incomeColors = React.useMemo(
    () => [
      theme.palette.chartMono.chartMono5,
      theme.palette.chartMono.chartMono4,
      theme.palette.chartMono.chartMono3,
      theme.palette.chartMono.chartMono2,
      theme.palette.chartMono.chartMono1,
      theme.palette.chartMono.chartMono6,
    ],
    [theme],
  ) as string[]

  const [selectedId, setSelectedId] = React.useState('')
  const [selectedTab, setSelectedTab] = React.useState(0)

  const spendingData = React.useMemo(
    () => getSpendingData(categories, goalsCopy, theme, dateRangeCategoryTotals),
    [categories, dateRangeCategoryTotals],
  )

  const incomeData = React.useMemo(
    () => getIncomeData(categories, dateRangeCategoryTotals, incomeColors),
    [categories, dateRangeCategoryTotals, incomeColors],
  )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setSelectedId('')
  }

  const handleSelected = (id: string) => {
    setSelectedId(selectedId === id ? '' : id)
  }

  return (
    <Box width="100%">
      <Tabs
        aria-label={spendingCopy.mini_title}
        onChange={handleTabChange}
        sx={{ touchAction: 'manipulation' }}
        value={selectedTab}
        variant="fullWidth"
      >
        <Tab
          aria-controls="spending-tabpanel"
          id="spending-tab"
          label={
            <SpendingTabLabel
              amount={spendingData.totalAmount}
              label={spendingCopy.spend_tab_title}
              variant="mini"
            />
          }
          tabIndex={selectedTab === 0 ? 0 : -1}
        />
        <Tab
          aria-controls="income-tabpanel"
          id="income-tab"
          label={
            <SpendingTabLabel
              amount={incomeData.totalAmount}
              label={spendingCopy.income_tab_title}
              variant="mini"
            />
          }
          tabIndex={selectedTab === 1 ? 0 : -1}
        />
      </Tabs>
      <Box
        aria-labelledby="spending-tab"
        hidden={selectedTab !== 0}
        id="spending-tabpanel"
        role="tabpanel"
      >
        <Stack direction="row" gap={16} justifyContent={'center'} p={24}>
          <SpendingDonut
            data={spendingData}
            onSelected={handleSelected}
            selectedId={selectedId}
            totalLabel={spendingCopy.total_spending}
          />

          <SpendingLegend data={spendingData} onSelected={handleSelected} selectedId={selectedId} />
        </Stack>
      </Box>
      <Box
        aria-labelledby="income-tab"
        hidden={selectedTab !== 1}
        id="income-tabpanel"
        role="tabpanel"
      >
        <Stack direction="row" gap={16} justifyContent={'center'} p={24}>
          <SpendingDonut
            data={incomeData}
            isIncome={true}
            onSelected={handleSelected}
            selectedId={selectedId}
            totalLabel={spendingCopy.total_income}
          />

          <SpendingLegend data={incomeData} onSelected={handleSelected} selectedId={selectedId} />
        </Stack>
      </Box>
    </Box>
  )
}

export default observer(MiniSpendingChart)
